/**
 * De foutmelding en foutcode uit een API-fout, voor wat de gebruiker te zien krijgt.
 *
 * De backend geeft een fout in een van deze vormen:
 *   - globale handler (prijs-/documentblokkade, 503):  { message, code, details, ... }
 *   - fout_antwoord (verzenden, PDF):                  { code, message, detail }
 *   - HTTPException met tekst:                         { detail: "..." }
 *   - HTTPException met object (GEEN_PRIJS):           { detail: "<melding>", code, message,
 *                                                        detail_data: { code, message, items } }
 *     (`detail` is altijd tekst; het object staat in detail_data)
 * Een scherm dat alleen `detail` las, toonde bij de eerste vorm alleen de axios-tekst
 * ("Request failed with status code 503") en nooit de code.
 *
 * Alleen weergave: hier wordt niets berekend.
 */

/** Het volledige detail-object van een fout ({code, message, items, ...}), of null.
 *  De backend zet het in `detail_data`; `detail` zelf is tekst. Een los object in
 *  `detail` (oudere vorm) wordt ook gelezen. */
export function foutData(err) {
  const data = err?.response?.data
  if (!data || typeof data !== 'object') return null
  if (data.detail_data && typeof data.detail_data === 'object') return data.detail_data
  return data.detail && typeof data.detail === 'object' ? data.detail : null
}

function uitData(data) {
  if (!data || typeof data !== 'object') return { code: null, message: null }
  const detail = data.detail
  const detailObj = (data.detail_data && typeof data.detail_data === 'object')
    ? data.detail_data
    : (detail && typeof detail === 'object' ? detail : null)
  const code = (typeof data.code === 'string' && data.code)
    || (detailObj && typeof detailObj.code === 'string' && detailObj.code) || null
  const message = (typeof data.message === 'string' && data.message)
    || (detailObj && typeof detailObj.message === 'string' && detailObj.message)
    || (typeof detail === 'string' && detail)
    || null
  return { code, message }
}

/** { code, message } uit een axios-fout (JSON-antwoord). */
export function foutMelding(err, standaard = 'Er is een fout opgetreden') {
  const { code, message } = uitData(err?.response?.data)
  return { code, message: message || err?.message || standaard }
}

/** Zoals foutMelding, ook als het antwoord een Blob is (responseType: 'blob'). */
export async function foutMeldingAsync(err, standaard = 'Er is een fout opgetreden') {
  const data = err?.response?.data
  if (typeof Blob !== 'undefined' && data instanceof Blob) {
    try {
      const { code, message } = uitData(JSON.parse(await data.text()))
      return { code, message: message || err?.message || standaard }
    } catch {
      return { code: null, message: err?.message || standaard }
    }
  }
  return foutMelding(err, standaard)
}

/** "melding (CODE)", of alleen de melding als er geen code is. */
export function foutTekst({ code, message }) {
  return code ? `${message} (${code})` : message
}

/** Kortweg: de tekst voor een toast of foutvak. */
export function foutTekstVan(err, standaard) {
  return foutTekst(foutMelding(err, standaard))
}

// Codes waarvoor het scherm een eigen vertaalde reden heeft (regel #670: de client toont de
// vertaling van de code, niet de ruwe backendmelding).
const VERTAALDE_CODES = {
  COMPOSIET_KORPUS_NIET_GEPRIJSD: 'offerteFiche.meubelen.composietKorpusNietGeprijsd',
  PANELEN_FOUT: 'offerteFiche.meubelen.panelenFout',
  ONDERDEEL_ZONDER_MATERIAAL: 'offerteFiche.meubelen.onderdeelZonderMateriaal',
  COMPOSIETLAAG_ZONDER_PRIJS: 'offerteFiche.meubelen.composietlaagZonderPrijs',
  SCHUINE_KAST_FOUT: 'offerteFiche.meubelen.schuineKastFout',
  KORPUS_PLAATKOST_NUL: 'offerteFiche.meubelen.korpusPlaatkostNul',
  FRONT_OPBOUW_PRIJS_NUL: 'offerteFiche.meubelen.frontOpbouwPrijsNul',
  FRONT_PLAAT_ZONDER_PRIJS: 'offerteFiche.meubelen.frontPlaatZonderPrijs',
  KLEUR_INKOOP_ONTBREEKT: 'offerteFiche.meubelen.kleurInkoopOntbreekt',
  FRONT_ZONDER_PRIJS: 'offerteFiche.meubelen.frontZonderPrijs',
  BTW_TARIEVEN_ONTBREKEN: 'offerteFiche.financieel.btwTarievenOntbreken',
}

// Codes van een ontbrekende DNA-instelling: ondanks hun code toont de interceptor de DNA-banner
// (een 503 met error_type DNA_NOT_CONFIGURED), want de gebruiker lost ze op in Master DNA.
export const DNA_BANNER_CODES = new Set(['BTW_TARIEVEN_ONTBREKEN'])

/** De i18n-sleutel van de vertaalde reden voor een bekende code, anders undefined. */
export function vertaaldeCodeSleutel(code) {
  return code ? VERTAALDE_CODES[code] : undefined
}

/** Vertaalde reden met code voor een regel die zelf een code draagt (bv. prijs_fout_code in een
 * lijst): de vertaling als de code bekend is, anders de reden van de backend. */
export function codeTekst(code, t, reden) {
  const sleutel = vertaaldeCodeSleutel(code)
  return `${sleutel ? t(sleutel) : (reden || code)} (${code})`
}

/** "<tekst> (<CODE>)" voor een regel waarvan de backend code en vaste tekst meestuurt
 * (prijs_foutteksten, besluit 2026-10-22, #809): de tekst van de backend, geen eigen vertaling. */
export function backendCodeTekst(code, tekst) {
  return `${tekst || code} (${code})`
}

/** Vertaalde reden met code ("<reden> (<CODE>)") voor een bekende code, anders foutTekstVan. */
export function vertaaldeFoutTekst(err, t, standaard) {
  const { code } = foutMelding(err, standaard)
  if (code && VERTAALDE_CODES[code]) return `${t(VERTAALDE_CODES[code])} (${code})`
  return foutTekstVan(err, standaard)
}
